import type { CaseTypeKey } from '@core/designsystem';
import type { Case, CaseEvent, CaseType, Client, Tag } from '@core/domain/model';
import { shortDayMonth, shortDayMonthTime } from '@core/ui/RuDates';
import type { CaseContext } from './sorting';
import type { CaseAccent, CaseListItem } from './types';

/**
 * Дело, его клиент и ближайшее событие → строка списка.
 *
 * Форматирование живёт здесь, а не в вёрстке: экран должен получать готовый текст,
 * иначе его нельзя ни отрисовать, ни проверить тестом без базы.
 */

/** Название типа — одно на всё приложение; у «Другого» показывается своё. */
export function typeLabel(item: Pick<Case, 'type' | 'typeOther'>): string {
  const own = item.typeOther?.trim();
  if (item.type === 'Other' && own) return own;

  const labels: Record<CaseType, string> = {
    Consultation: 'Консультация',
    ClaimWork: 'Претензионная работа',
    CourtCase: 'Судебное дело',
    Enforcement: 'Исп. производство',
    Other: 'Другое',
  };
  return labels[item.type];
}

/** Домен → оформление: дизайн-система не знает про типы дел ([D-09]). */
export function typeAccent(type: CaseType): CaseTypeKey {
  const accents: Record<CaseType, CaseTypeKey> = {
    CourtCase: 'court',
    ClaimWork: 'claim',
    Consultation: 'consultation',
    Enforcement: 'enforcement',
    Other: 'other',
  };
  return accents[type];
}

/** Фамилия плюс имя, либо фамилия, имя и отчество. */
const PERSON_NAME_PARTS = [2, 3];

/** Кавычки в наименовании — верный признак организации, а не человека. */
const ORGANISATION_MARKS = /[«»"']/;

/**
 * «Иванов Иван Иванович» → «Иванов И. И.»; наименование организации остаётся как есть.
 *
 * Сокращаем, только когда перед нами похоже на ФИО: две или три части, каждая
 * с заглавной буквы, без цифр и кавычек. Иначе «ООО «Сидоров и Ко»» превращается
 * в «ООО и. К.» — найдено на живых данных на устройстве.
 */
export function shortName(fullName: string): string {
  const trimmed = fullName.trim();
  const parts = trimmed.split(' ').filter(Boolean);

  const looksLikePerson =
    PERSON_NAME_PARTS.includes(parts.length) &&
    !ORGANISATION_MARKS.test(trimmed) &&
    parts.every((part) => {
      const first = part[0] ?? '';
      return first === first.toUpperCase() && first !== first.toLowerCase() && !/\d/.test(part);
    });

  if (!looksLikePerson) return trimmed;

  const initials = parts
    .slice(1)
    .map((part) => part[0])
    .filter((letter): letter is string => Boolean(letter))
    .map((letter) => `${letter}.`);

  return initials.length === 0 ? trimmed : [parts[0], ...initials].join(' ');
}

const eventLabel = (event: CaseEvent) =>
  event.type === 'Hearing' ? 'Заседание' : event.type === 'Meeting' ? 'Встреча' : event.title;

/** У задачи со сроком время обычно не важно — показываем только дату. */
function eventTime(event: CaseEvent): string {
  if (event.type === 'Task' && event.deadlineAt !== null) {
    return `до ${shortDayMonth(new Date(event.deadlineAt))}`;
  }
  return shortDayMonthTime(new Date(event.startAt));
}

/**
 * Клиент, тип и теги одной строкой.
 *
 * Отдельной строки теги не получают: строка дела и так держит три строки под плиткой
 * типа ([D-09]), а четвёртая сломала бы ритм списка. Кто ищет дела по тегу, пользуется
 * чипом фильтра, а не читает метки в строке.
 */
const subtitle = (item: Case, client: Client | null, tags: Tag[]) =>
  [client ? shortName(client.fullName) : null, typeLabel(item), ...tags.map((tag) => tag.name)]
    .filter((part): part is string => Boolean(part))
    .join(' · ');

export function toListItem(
  context: CaseContext,
  client: Client | null,
  tags: Tag[] = [],
): CaseListItem {
  const item = context.item;

  let statusLine: string;
  let accent: CaseAccent;

  if (context.overdue) {
    // Просрочка важнее всего остального: ради этого юрист и открывает список.
    const title = context.overdue.title;
    statusLine = `Просрочено: ${title.charAt(0).toLowerCase()}${title.slice(1)}`;
    accent = 'overdue';
  } else if (item.status === 'Suspended') {
    statusLine = 'Приостановлено';
    accent = 'paused';
  } else if (item.status === 'Completed') {
    statusLine = 'Завершено';
    accent = 'completed';
  } else if (context.upcoming) {
    statusLine = `${eventLabel(context.upcoming)} ${eventTime(context.upcoming)}`;
    accent = 'active';
  } else {
    statusLine = 'Задач нет';
    accent = 'active';
  }

  return {
    id: item.id,
    title: item.title,
    subtitle: subtitle(item, client, tags),
    statusLine,
    accent,
    typeAccent: typeAccent(item.type),
    isPinned: item.isPinned,
  };
}
